import React, { useState, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { Drawer, Button, Input, Upload, message, Modal, Spin, Radio, Segmented, Space, Tooltip } from 'antd';
import {
  InboxOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
  ArrowRightOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  uploadResume,
  reviewResume,
  createSearchPlan,
  createSearchBatch,
  createResumeVersion,
  getSearches,
} from '../../services/api';
import {
  ResumeUploadResponse,
  ResumeReview,
  Readiness,
  SearchQuota,
} from '../../types';
import {
  SearchDraft,
  saveSearchDraft,
  loadSearchDraft,
  clearSearchDraft,
} from '../../utils/searchDraft';
import {
  ApiErrorCode,
  getApiErrorCode,
  getApiErrorMessage,
  isNotFoundError,
} from '../../utils/errorUtils';
import { AUTO_LANGUAGE } from '../../utils/languages';
import { captureEvent, setPersonProps } from '../../services/analytics';
import LanguagePicker from '../localization/LanguagePicker';
import StepProgress from './StepProgress';
import ResumeReviewStep from './review/ResumeReviewStep';
import CoveragePlanStep, { PlanAgent } from './CoveragePlanStep';
import css from './SearchDrawer.module.scss';

const { Dragger } = Upload;

interface SearchDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (ids: string[]) => void;
  onAfterClose?: () => void;
}

type Step = 'upload' | 'confirm' | 'review' | 'plan';

const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// Fast-path client guard. The backend is the source of truth (it validates by
// file *content*), so this only weeds out obviously-wrong picks before upload.
const ACCEPTED_RESUME_MIME_TYPES = ['application/pdf', DOCX_MIME];

// `accept` for the picker — extensions + MIME types for cross-browser UX only.
// Note: .doc (legacy binary Word) is intentionally excluded; only .docx.
const RESUME_ACCEPT = `.pdf,.docx,application/pdf,${DOCX_MIME}`;

const RESUME_FORMAT_ERROR = 'Please upload a PDF or DOCX file';

// Mirrors how antd/rc-upload matches files against `accept` (MIME or extension),
// so the drop handler can tell when a drop will be silently rejected.
const isAcceptedResume = (file: File): boolean =>
  (!!file.type && ACCEPTED_RESUME_MIME_TYPES.includes(file.type)) ||
  /\.(pdf|docx)$/i.test(file.name);

// On a non-`directory` Dragger, rc-upload filters dropped files against `accept`
// *before* beforeUpload runs, so a drop of only unsupported files would show no
// feedback at all (unlike the click path). Surface the same friendly message.
const handleResumeDrop = (e: React.DragEvent<HTMLDivElement>) => {
  const dropped = Array.from(e.dataTransfer?.files ?? []);
  if (dropped.length > 0 && !dropped.some(isAcceptedResume)) {
    message.error(RESUME_FORMAT_ERROR);
  }
};

// Creating a pack is one synchronous request that can outlast the ~125s
// Cloudflare waits in front of the API (prod 2026-10-19/20: two new users'
// first packs). The browser then gets a bare "Network Error" -- Cloudflare's
// 524 carries no CORS headers -- or a gateway status, while the API is still
// creating the searches. That answer means "unknown", not "failed": showing
// an error sent both users to press again, three and four times. Instead the
// drawer names the pack itself (groupId, which the API stamps on every search
// it creates for this request) and watches the search list for that pack to
// land. The API now bounds its own LLM waits at ~80s, so a pack that is coming
// lands well inside the window.
//
// Mutable on purpose: the tests shrink the timings.
export const packRecovery = {
  pollMs: 10_000,
  // Wall-clock cap on the whole watch, whatever the individual looks cost.
  windowMs: 180_000,
  // One look at the list; a hung GET must not stretch the window.
  lookTimeoutMs: 15_000,
  // Each search commits on its own, up to ~45s apart (the API's contact-roles
  // deadline), so the first sighting is not the whole pack: stop early only
  // when every item is in, or after this many looks without a new one.
  settlePolls: 6,
};

// A resume upload takes ~10-15s, far inside Cloudflare's origin timeout, so a
// lost answer there is a transport failure, not a slow API (prod 2026-11-01:
// the API stored the upload, the browser reported "Network Error"). Repeating
// an upload costs one more parse but changes nothing the user can see --
// nothing starts until they create a search -- so the drawer sends the same
// file once more before reporting an error.
//
// Mutable on purpose: the tests shrink the pause.
export const uploadRetry = { pauseMs: 1_500 };

const GATEWAY_STATUSES = new Set([502, 504, 520, 521, 522, 523, 524]);

// No response at all (a request that was sent but never answered), or a
// gateway's answer rather than the API's own. The API's own 500/503 carry a
// real verdict and are reported as before.
const isLostResponse = (error: any): boolean =>
  error?.response
    ? GATEWAY_STATUSES.has(error.response.status)
    : !!error?.request;

const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timed out')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

// The ids of this pack's searches as they land -- the searches stamped with
// the groupId this attempt sent, so nothing created elsewhere can pass for
// them. Empty when nothing lands before the window closes, or when
// `isCurrent` turns false (the drawer closed, or a newer attempt started).
const waitForPack = async (
  groupId: string,
  expected: number,
  isCurrent: () => boolean
): Promise<string[]> => {
  const deadline = Date.now() + packRecovery.windowMs;
  let found: string[] = [];
  let quietLooks = 0;
  while (isCurrent() && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, packRecovery.pollMs));
    if (!isCurrent()) return [];
    let searches;
    try {
      ({ searches } = await withTimeout(
        getSearches({ limit: 100 }),
        packRecovery.lookTimeoutMs
      ));
    } catch (error: any) {
      // Signed out: the response interceptor is already sending them to sign in.
      if (error?.response?.status === 401) break;
      // Any other failed look is not an answer; keep watching.
      continue;
    }
    const fresh = searches.filter((s) => s.groupId === groupId).map((s) => s.id);
    if (fresh.length > found.length) {
      found = fresh;
      quietLooks = 0;
    } else if (found.length > 0) {
      quietLooks += 1;
    }
    if (found.length >= expected) break;
    if (found.length > 0 && quietLooks >= packRecovery.settlePolls) break;
  }
  return isCurrent() ? found : [];
};

const isValidField = (value: string): boolean => {
  const len = value.trim().length;
  return len >= 2 && len <= 100;
};

// Rationale markers for cards the user owns rather than the Beast. Kept as named
// constants (not inline strings) so the edit logic can recognise a user-authored
// card without string-matching against copy that might later drift.
const AUTHORED_RATIONALE = 'Added by you.';
const EDITED_RATIONALE = 'Edited by you.';
const USER_AUTHORED_RATIONALES = new Set([AUTHORED_RATIONALE, EDITED_RATIONALE]);

// Remote-search destinations offered as one-click presets. `Worldwide` and
// `Europe` are contract strings: the crawler keys its board queries on them.
const DESTINATION_PRESETS = ['United States', 'Europe', 'Worldwide'] as const;
// The REGION presets: no single country, so no single permit to ask about —
// one Yes for "Europe" would otherwise be applied to jobs in four countries.
// The API skips the permit logic for exactly these two strings too.
const REGION_PRESETS = ['worldwide', 'europe'];
const isRegionPreset = (value: string) => REGION_PRESETS.includes(value.trim().toLowerCase());
const sameCountry = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

const PERMIT_HINT =
  "Remote doesn't always mean from anywhere. Many remote roles still require you to " +
  "live in, or be allowed to work in, the employer's country. Your answer lets us " +
  'skip those and keep the ones open to you.';

const SearchDrawer: React.FC<SearchDrawerProps> = ({ open, onClose, onSuccess, onAfterClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('upload');
  const [loading, setLoading] = useState(false); // resume upload
  const [reviewLoading, setReviewLoading] = useState(false); // /review call
  const [resumeData, setResumeData] = useState<ResumeUploadResponse | null>(null);
  const [fileName, setFileName] = useState<string>('');
  const [role, setRole] = useState('');
  const [location, setLocation] = useState('');
  const [draftLanguage, setDraftLanguage] = useState<string>(AUTO_LANGUAGE);
  // Remote search (design 2026-09-16). `location` keeps ONE meaning — where the
  // user wants to work — and is merely labelled "Destination" in remote mode.
  const [isRemote, setIsRemote] = useState(false);
  const [residence, setResidence] = useState('');
  // Tri-state: true / false / null for "not answered". There is no honest
  // default — a defaulted no tells the API the user may not work in the
  // destination, which buries every role there.
  const [hasWorkPermit, setHasWorkPermit] = useState<boolean | null>(null);

  // The permit question only makes sense when the user lives outside a single
  // destination country; a Worldwide search has no one country to ask about.
  // Living in the destination is itself the answer — yes — so there is nothing
  // to ask and nothing to store. Storing it is what would be unsafe: a residence
  // later edited away from the destination would inherit that yes into a
  // question the user never saw. Nothing else is available to prefill from
  // either; the resume upload carries desiredRole/desiredLocation and the review
  // carries readiness/summary/fixes, and neither extracts work authorization.
  const asksPermit =
    isRemote &&
    residence.trim() !== '' &&
    !isRegionPreset(location) &&
    !sameCountry(location, residence);
  // A shown question must be answered by the user: an unapproved guess may never
  // become a claim, so the step stays closed until they pick one.
  const permitUnanswered = asksPermit && hasWorkPermit === null;

  // Review state
  const [review, setReview] = useState<ResumeReview | null>(null);
  const [previousReadiness, setPreviousReadiness] = useState<Readiness | null>(null);
  const [reviewError, setReviewError] = useState<'expired' | 'unavailable' | null>(null);
  const [lastReviewedKey, setLastReviewedKey] = useState<string | null>(null);

  // Coverage-plan state (Multi-Agent Search)
  const [planItems, setPlanItems] = useState<PlanAgent[]>([]);
  // Broadened agents set aside by "Just my exact search", so the collapse is
  // non-destructive and one-click reversible.
  const [stashedAgents, setStashedAgents] = useState<PlanAgent[]>([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState(false);
  const [batchCreating, setBatchCreating] = useState(false);
  const [quota, setQuota] = useState<SearchQuota | null>(null);

  // Tailoring handoff state: an APPROVED version to bind at creation (set by a
  // restored draft after the editor's approve), and the in-flight guard for
  // the "Tailor my resume" entry call.
  const [resumeVersionId, setResumeVersionId] = useState<string | null>(null);
  const [tailorEntering, setTailorEntering] = useState(false);

  // Post-create success panel. Set (instead of closing immediately) once the
  // batch creates ≥1 search, so the user leaves knowing what happens next.
  // `dailyTotal` is the PER-USER budget — the whole account's daily allowance
  // from day 2 onward, not a per-search figure. Deliberately not
  // maxDailyOpportunitiesPerSearch: that one is first-run only and multiplies
  // by pack size, so quoting it in an "after that…" sentence overstated the
  // ongoing cadence by up to 5x. Null when an older backend omits the field —
  // the copy then renders without a number rather than inventing one.
  const [creationSuccess, setCreationSuccess] = useState<{
    ids: string[];
    summary: string;
    dailyTotal: number | null;
  } | null>(null);

  // True only after a search (single or pack) is successfully created, so the
  // subsequent close isn't logged as an abandonment.
  const succeeded = useRef(false);

  // Identity of the latest create or upload. Bumped by every new create or
  // upload and by every reset (close, handoff) and unmount, so a lost-response
  // watch, or an upload, that is still running can tell it no longer owns the
  // drawer and must touch nothing.
  const createAttempt = useRef(0);
  const hideRecoveryNotice = useRef<(() => void) | null>(null);
  const abandonRecovery = useCallback(() => {
    createAttempt.current += 1;
    hideRecoveryNotice.current?.();
    hideRecoveryNotice.current = null;
  }, []);
  useEffect(() => abandonRecovery, [abandonRecovery]);

  // Monotonic id source so each plan agent has a stable identity across edits,
  // removals and restores — never key a mutable list by its array index.
  const agentSeq = useRef(0);
  const nextAgentId = () => `agent-${agentSeq.current++}`;

  const resetState = useCallback(() => {
    abandonRecovery();
    setStep('upload');
    setLoading(false);
    setReviewLoading(false);
    setResumeData(null);
    setFileName('');
    setRole('');
    setLocation('');
    setDraftLanguage(AUTO_LANGUAGE);
    setIsRemote(false);
    setResidence('');
    setHasWorkPermit(null);
    setReview(null);
    setPreviousReadiness(null);
    setReviewError(null);
    setLastReviewedKey(null);
    setPlanItems([]);
    setStashedAgents([]);
    setPlanLoading(false);
    setPlanError(false);
    setBatchCreating(false);
    setQuota(null);
    setResumeVersionId(null);
    setTailorEntering(false);
    setCreationSuccess(null);
  }, [abandonRecovery]);

  const finishClose = () => {
    if (!succeeded.current && (step !== 'upload' || !!resumeData)) {
      captureEvent('search_creation_abandoned', { last_step: step });
    }
    succeeded.current = false;
    resetState();
    onClose();
  };

  // Leave the success panel: the exact close+notify+navigate the pre-panel
  // flow ran right after creation. The draft was already cleared and
  // `succeeded` set when the batch succeeded, so finishClose logs no
  // abandonment and there is nothing left to discard.
  const handleSuccessDone = () => {
    const ids = creationSuccess?.ids ?? [];
    finishClose();
    onSuccess(ids);
  };

  // Explicit close discards the handoff draft. When an APPROVED version would
  // be dropped with it (approved in the editor but not yet bound to a search),
  // confirm first — silently losing that work is worse than one modal.
  const handleClose = () => {
    // Any close from the success panel (X / Esc / mask) is "Done": after a
    // successful create the old flow always notified + navigated, and the
    // searches list must learn about its new members either way.
    if (creationSuccess) {
      handleSuccessDone();
      return;
    }
    if (!succeeded.current && resumeVersionId) {
      Modal.confirm({
        title: 'Discard your tailored resume?',
        content:
          "Your tailored resume isn't attached to a search yet. Discard?",
        okText: 'Discard',
        cancelText: 'Keep working',
        onOk: () => {
          clearSearchDraft();
          finishClose();
        },
      });
      return;
    }
    clearSearchDraft();
    finishClose();
  };

  // Close silently for an in-app handoff to the tailoring editor: the draft
  // was just saved (or is intentionally kept), so no clear, no abandonment
  // event, no discard prompt — the user is continuing the flow, not leaving it.
  const closeForHandoff = () => {
    succeeded.current = false;
    resetState();
    onClose();
  };

  const handleFileUpload = async (file: File) => {
    // When the browser reports no MIME type, defer to the backend rather than
    // block a possibly-valid file — content-based validation is authoritative.
    if (file.type && !ACCEPTED_RESUME_MIME_TYPES.includes(file.type)) {
      message.error(RESUME_FORMAT_ERROR);
      return false;
    }

    setLoading(true);
    setFileName(file.name);
    createAttempt.current += 1;
    const attempt = createAttempt.current;
    const isCurrent = () => createAttempt.current === attempt;

    const uploadStart = performance.now();
    let retried = false;
    try {
      let response: ResumeUploadResponse;
      try {
        response = await uploadResume(file);
      } catch (error) {
        if (!isLostResponse(error)) throw error;
        retried = true;
        await new Promise((resolve) => setTimeout(resolve, uploadRetry.pauseMs));
        if (!isCurrent()) return false;
        response = await uploadResume(file);
      }
      // Closed or reset while uploading: this upload owns nothing now.
      if (!isCurrent()) return false;
      captureEvent('resume_uploaded', {
        file_size_bytes: file.size,
        file_type: file.type,
        upload_duration_ms: Math.round(performance.now() - uploadStart),
        retried_after_lost_response: retried,
      });
      setResumeData(response);
      // Preserve the user's confirmed target across re-uploads: only fill
      // empty fields from the new upload's suggestions.
      if (!role) setRole(response.desiredRole || '');
      if (!location) setLocation(response.desiredLocation || '');
      // Residence is deliberately NOT prefilled from desiredLocation: that is
      // the resume's TARGET, not a home. A resume aimed at the US from Israel
      // would fill both fields with "United States", suppress the permit
      // question, and grade every ad as if the user lived there — silently.
      setStep('confirm');
      message.success('Resume analyzed successfully');
    } catch (error) {
      if (!isCurrent()) return false;
      console.error('Resume upload error:', error);
      // A corrupt/renamed/unsupported file now comes back as a 400 with this
      // code; show the same friendly copy as the client-side guard.
      if (getApiErrorCode(error) === ApiErrorCode.UnsupportedResumeFormat) {
        message.error(RESUME_FORMAT_ERROR);
      } else {
        message.error(getApiErrorMessage(error));
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }

    return false; // Prevent default upload behavior
  };

  // Parameterized core so a restored draft can run it directly — reading the
  // just-hydrated values from state in the same tick would see stale closures.
  const runReview = async (
    resumeInternalId: string,
    trimmedRole: string,
    trimmedLocation: string
  ) => {
    setStep('review');
    setReviewError(null);
    setReviewLoading(true);
    try {
      const result = await reviewResume(resumeInternalId, {
        role: trimmedRole,
        location: trimmedLocation,
      });
      setReview(result);
      setLastReviewedKey(`${resumeInternalId}|${trimmedRole}|${trimmedLocation}`);
      captureEvent('resume_review_completed', {
        readiness: result.readiness,
        fix_count: result.fixes?.length ?? 0,
      });
    } catch (error) {
      console.error('Resume review error:', error);
      const reviewErrorKind = isNotFoundError(error) ? 'expired' : 'unavailable';
      setReviewError(reviewErrorKind);
      if (reviewErrorKind === 'unavailable') {
        captureEvent('resume_review_unavailable', {});
      }
    } finally {
      setReviewLoading(false);
    }
  };

  const handleRunReview = async () => {
    if (!resumeData) return;
    if (!isValidField(role) || !isValidField(location)) {
      message.error('Role and location must each be 2–100 characters.');
      return;
    }
    if (isRemote && !isValidField(residence)) {
      message.error('Where you are based must be 2–100 characters.');
      return;
    }
    if (permitUnanswered) {
      message.error(`Tell us whether you can work in ${location.trim()} — we won't guess.`);
      return;
    }

    const trimmedRole = role.trim();
    const trimmedLocation = location.trim();
    const key = `${resumeData.resumeInternalId}|${trimmedRole}|${trimmedLocation}`;

    // Client memo: nothing changed since the last successful review → reuse it.
    if (key === lastReviewedKey && review) {
      setStep('review');
      setReviewError(null);
      return;
    }

    await runReview(resumeData.resumeInternalId, trimmedRole, trimmedLocation);
  };

  // Multi-Agent Search — generate the coverage plan from the seed. The review is
  // anchored on this same seed; the plan broadens it into adjacent role/geo agents.
  // Parameterized for the same restored-draft reason as runReview.
  const generatePlan = async (
    resumeInternalId: string,
    trimmedRole: string,
    trimmedLocation: string,
    remote: boolean
  ) => {
    setStep('plan');
    setPlanError(false);
    setPlanLoading(true);
    setStashedAgents([]); // a freshly generated plan starts without a stashed undo
    try {
      const resp = await createSearchPlan({
        resumeInternalId,
        role: trimmedRole,
        location: trimmedLocation,
        // A remote seed is broadened by role only; a local request is unchanged.
        ...(remote ? { isRemote: true } : {}),
      });
      // Item 0 is always the user's exact seed — keep it read-only in the UI.
      // A remote plan varies by role only: the API pins every item to the seed
      // destination, and so does this, so an older API (or a model that
      // narrowed "Worldwide" to "Germany") cannot hand a pre-selected card a
      // market the user never asked for.
      setPlanItems(
        resp.items.map((it, idx) => ({
          id: nextAgentId(),
          role: it.role,
          location: remote ? trimmedLocation : it.location,
          rationale: it.rationale,
          seed: idx === 0,
        }))
      );
      setQuota(resp.quota);
      captureEvent('coverage_plan_generated', { item_count: resp.items.length });
    } catch (error) {
      console.error('Coverage plan error:', error);
      setPlanError(true);
    } finally {
      setPlanLoading(false);
    }
  };

  const handleGeneratePlan = async () => {
    if (!resumeData) return;
    await generatePlan(resumeData.resumeInternalId, role.trim(), location.trim(), isRemote);
  };

  // Hydrate the drawer from a handoff draft and jump back to where the user
  // left off. The draft stays in storage until creation succeeds or the user
  // explicitly closes — a mid-flow reload must stay restorable.
  const restoreDraft = (draft: SearchDraft) => {
    setResumeData(draft.resumeData);
    setFileName(draft.fileName ?? 'your resume');
    setRole(draft.role);
    setLocation(draft.location);
    setDraftLanguage(draft.draftLanguage || AUTO_LANGUAGE);
    // Drafts saved before remote search carry none of these → a local search.
    const remote = draft.isRemote === true;
    setIsRemote(remote);
    setResidence(draft.residence ?? '');
    // Absent on a pre-feature draft and on one saved before the question was
    // answered — both mean "not answered", which is not the same as No.
    setHasWorkPermit(typeof draft.hasWorkPermit === 'boolean' ? draft.hasWorkPermit : null);
    setReview(draft.review);
    if (draft.review) {
      setLastReviewedKey(
        `${draft.resumeData.resumeInternalId}|${draft.role}|${draft.location}`
      );
    }
    if (draft.resumeVersionId) setResumeVersionId(draft.resumeVersionId);

    if (draft.returnStep === 'plan') {
      void generatePlan(
        draft.resumeData.resumeInternalId,
        draft.role,
        draft.location,
        remote
      );
    } else if (draft.returnStep === 'review') {
      if (draft.review) {
        setStep('review');
      } else {
        // Returned from the editor without a stored review (e.g. entered
        // tailoring from the review-unavailable state) — run it fresh; the
        // backend memoizes so a repeat is cheap.
        void runReview(
          draft.resumeData.resumeInternalId,
          draft.role,
          draft.location
        );
      }
    } else {
      // Unknown returnStep — including 'tailor-confirm' drafts persisted by
      // the removed start-from-tailored flow. A draft that already carries an
      // approved version goes straight to the plan; otherwise land on confirm.
      if (draft.resumeVersionId) {
        void generatePlan(
          draft.resumeData.resumeInternalId,
          draft.role,
          draft.location,
          remote
        );
      } else {
        setStep('confirm');
      }
    }
  };

  // Review step → full-page tailoring editor. Persist the drawer state first
  // (navigation unmounts nothing — the drawer is global — but closing it
  // resets state), then hand off. On failure stay put: the review is intact.
  const handleEnterTailor = async () => {
    if (!resumeData) return;
    setTailorEntering(true);
    try {
      const version = await createResumeVersion({
        resumeInternalId: resumeData.resumeInternalId,
        role: role.trim(),
        location: location.trim(),
      });
      try {
        saveSearchDraft({
          resumeData,
          fileName,
          role: role.trim(),
          location: location.trim(),
          draftLanguage,
          isRemote,
          residence: residence.trim(),
          ...(hasWorkPermit !== null ? { hasWorkPermit } : {}),
          review,
          returnStep: 'review',
        });
      } catch (storageError) {
        // Storage can throw (quota, privacy modes). The server-side draft
        // exists either way — continue; the drawer just starts clean later.
        console.warn('SearchDrawer: could not persist the search draft', storageError);
      }
      captureEvent('resume_tailor_entered');
      closeForHandoff();
      navigate(`/resume/tailor/${version.id}`);
    } catch (error) {
      console.error('Tailor entry error:', error);
      message.error(getApiErrorMessage(error));
    } finally {
      setTailorEntering(false);
    }
  };

  // Activation funnel: log each drawer open; reset the success guard per open.
  // A stored handoff draft (saved when the user left for the tailoring editor)
  // takes over the fresh-start flow: hydrate and jump back to where they were.
  useEffect(() => {
    if (open) {
      succeeded.current = false;
      captureEvent('search_drawer_opened');
      const draft = loadSearchDraft();
      if (draft) {
        restoreDraft(draft);
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- open is the only real trigger; restoreDraft is a per-render closure and listing it would re-fire the effect mid-flow

  const removePlanItem = (id: string) =>
    setPlanItems((prev) => prev.filter((a) => a.id !== id));

  // "Just my exact search": stash the broadened agents (non-destructive) and show
  // only the seed. Never creates on its own — the user still commits via Start.
  const collapseToSeed = () => {
    setStashedAgents(planItems.filter((a) => !a.seed));
    setPlanItems((prev) => prev.filter((a) => a.seed));
  };

  // Undo the collapse: merge the stashed suggestions back, skipping any the user
  // has since re-created (matched by role+location), then clear the stash.
  const roleLocKey = (a: PlanAgent) =>
    `${a.role.trim().toLowerCase()}|${a.location.trim().toLowerCase()}`;
  const restorePlan = () => {
    setPlanItems((prev) => {
      const present = new Set(prev.map(roleLocKey));
      return [...prev, ...stashedAgents.filter((a) => !present.has(roleLocKey(a)))];
    });
    setStashedAgents([]);
  };

  const editPlanItem = (id: string, newRole: string, newLocation: string) =>
    setPlanItems((prev) =>
      prev.map((a) => {
        if (a.id !== id) return a;
        // Once the user overrides a Beast suggestion, its machine-written "why" no
        // longer describes the card — swap it for an honest provenance marker. Only
        // on a real change, and never over a card the user already owns (so a fresh
        // "Added by you." card doesn't flip to "Edited by you." on its first commit).
        const changed = a.role.trim() !== newRole || a.location.trim() !== newLocation;
        const rationale =
          changed && !USER_AUTHORED_RATIONALES.has(a.rationale)
            ? EDITED_RATIONALE
            : a.rationale;
        return { ...a, role: newRole, location: newLocation, rationale };
      })
    );

  // A remote pack is role variants of ONE destination, so an added card
  // inherits it (and the plan step hides the location field, see
  // CoveragePlanStep.fixedLocation). Locally the user types both.
  const addPlanItem = () =>
    setPlanItems((prev) => [
      ...prev,
      {
        id: nextAgentId(),
        role: '',
        location: isRemote ? location.trim() : '',
        rationale: AUTHORED_RATIONALE,
        seed: false,
      },
    ]);

  // Every card shown is included; only those with a valid role+location are sent.
  const selectableAgents = planItems.filter(
    (a) => isValidField(a.role) && isValidField(a.location)
  );
  const selectedCount = selectableAgents.length;
  const canAdd = quota
    ? selectedCount < Math.min(quota.maxPerBatch, quota.remaining)
    : false;

  // Every card in a remote pack carries the destination the permit question
  // named (API-returned items are pinned on arrival, edited and added cards
  // cannot change it), so the one answer rides on all of them. An unanswered
  // question is omitted, never guessed — the API then treats it as unknown and
  // judges by residence, which cannot wrongly exclude. That case is reachable
  // without the question ever being shown: a restored draft returning to the
  // plan step skips confirm. Unknown is the one value that is always safe to
  // send unasked.
  const remoteFields = isRemote
    ? {
        isRemote: true,
        residence: residence.trim(),
        ...(asksPermit && hasWorkPermit !== null ? { hasWorkPermit } : {}),
      }
    : {};

  // A permit answer belongs to the destination it was given for; a new
  // destination (typed or preset) asks afresh, unanswered. Reset in either
  // mode: a location changed while local is still a new destination when the
  // user switches back to remote.
  const changeLocation = (value: string) => {
    if (value !== location) setHasWorkPermit(null);
    setLocation(value);
  };

  const startedLabel = (count: number) =>
    `${count} ${count === 1 ? 'search' : 'searches'} started`;

  const showPackCreated = (ids: string[], summary: string, dailyTotal: number | null) => {
    // $set_once: records the user's activation timing (writes only on first search).
    setPersonProps(undefined, { first_search_created_at: new Date().toISOString() });
    // The handoff draft is spent: the resume cache is consumed server-side
    // and any bound version now belongs to the created searches.
    clearSearchDraft();
    succeeded.current = true;
    // Swap the drawer body to the success panel instead of closing — the
    // user leaves via "Done", which runs the same close+navigate as before.
    setCreationSuccess({ ids, summary, dailyTotal });
  };

  // Create the whole pack. Partial success: each item reports its own status.
  const handleCreatePack = async () => {
    if (!resumeData || selectedCount === 0) return;

    // Per the API contract, omit draftLanguage unless the user picked a real
    // language — "auto" is the server-side default, so sending it is noise.
    const explicitLanguage = draftLanguage !== AUTO_LANGUAGE;
    const searches = selectableAgents.map((a) => ({
      role: a.role.trim(),
      location: a.location.trim(),
      ...(explicitLanguage ? { draftLanguage } : {}),
      ...remoteFields,
    }));

    createAttempt.current += 1;
    const attempt = createAttempt.current;
    const isCurrent = () => createAttempt.current === attempt;
    setBatchCreating(true);
    // Named here, not by the server, so a lost response can still find it.
    const groupId = uuidv4();
    try {
      const resp = await createSearchBatch({
        resumeInternalId: resumeData.resumeInternalId,
        searches,
        groupId,
        // ONE shared version id at the batch top level (server clones it per
        // created item). Its seed role rides alongside so the backend can
        // auto-tailor each created search whose role differs from the seed.
        // Both gated together — the backend dispatches auto-tailor only when
        // BOTH are present.
        ...(resumeVersionId ? { resumeVersionId, seedRole: role.trim() } : {}),
      });
      // Closed or superseded while the create ran: this attempt owns nothing now.
      if (!isCurrent()) return;
      const created = resp.results.filter((r) => r.status === 'created').length;
      const dupes = resp.results.filter((r) => r.status === 'skipped_duplicate').length;
      const limited = resp.results.filter((r) => r.status === 'skipped_limit').length;

      captureEvent('search_pack_created', {
        requested: searches.length,
        created,
        skipped_duplicate: dupes,
        skipped_limit: limited,
        draft_language: draftLanguage,
        review_viewed: review !== null,
        readiness: review?.readiness ?? null,
        tailored_resume: !!resumeVersionId,
      });

      if (created > 0) {
        const parts = [startedLabel(created)];
        if (dupes > 0) parts.push(`${dupes} already running`);
        if (limited > 0) parts.push(`${limited} over your limit`);
        showPackCreated(
          resp.results.filter((r) => r.status === 'created' && r.searchId != null)
            .map((r) => r.searchId as string),
          parts.join(' · '),
          resp.maxDailyOpportunitiesPerUser ?? null
        );
      } else {
        // Nothing was created → the backend keeps the resume, so stay open for a
        // retry rather than dead-ending the user.
        if (limited > 0 && dupes === 0) {
          message.warning(
            "You're at your search limit — pause or delete a search to add more."
          );
        } else {
          message.info('Those searches are already running.');
        }
      }
    } catch (error) {
      if (!isCurrent()) return;
      if (isLostResponse(error)) {
        hideRecoveryNotice.current = message.loading('Still setting up your searches…', 0);
        const fresh = await waitForPack(groupId, searches.length, isCurrent);
        // Closed or superseded while watching: this attempt owns nothing now.
        if (!isCurrent()) return;
        hideRecoveryNotice.current?.();
        hideRecoveryNotice.current = null;
        if (fresh.length > 0) {
          captureEvent('search_pack_created', {
            requested: searches.length,
            created: fresh.length,
            recovered_after_lost_response: true,
            draft_language: draftLanguage,
            review_viewed: review !== null,
            readiness: review?.readiness ?? null,
            tailored_resume: !!resumeVersionId,
          });
          // The response that would have carried the daily budget never
          // arrived; the panel simply omits that line.
          showPackCreated(fresh, startedLabel(fresh.length), null);
          return;
        }
      }
      console.error('Create pack error:', error);
      message.error(getApiErrorMessage(error));
    } finally {
      if (isCurrent()) setBatchCreating(false);
    }
  };

  const handleReupload = () => {
    // Stash the readiness the user just saw so the next review can show a delta.
    setPreviousReadiness(review?.readiness ?? null);
    setReview(null);
    setReviewError(null);
    setLastReviewedKey(null);
    setResumeData(null);
    setFileName('');
    setResumeVersionId(null); // a new upload invalidates a version tuned for the old one
    setStep('upload');
    // role / location / draftLanguage / remote mode are intentionally preserved
  };

  const handleBackToUpload = () => {
    setStep('upload');
    setResumeData(null);
    setFileName('');
    setReview(null);
    setReviewError(null);
    setLastReviewedKey(null);
    setPreviousReadiness(null);
    setResumeVersionId(null);
  };

  const handleBackToConfirm = () => {
    setStep('confirm');
    // A role/location change is a new target, not an improved re-upload —
    // don't claim a delta against the previous upload.
    setPreviousReadiness(null);
    // An attached version was tailored for the PREVIOUS target — unbind it
    // rather than silently sending a resume tuned for the wrong role. (The
    // approved version itself is kept server-side and stays reusable.)
    setResumeVersionId(null);
  };

  // Sticky footer actions per step / sub-state. Kept here (not in the step
  // bodies) so the primary action is always reachable without scrolling. The
  // review never blocks progress: a forward path exists in every state except
  // `expired` (the resume id is gone server-side, so re-upload is the only path).
  const renderFooter = () => {
    if (creationSuccess) {
      return (
        <div className={css.footer}>
          <Button type="primary" block onClick={handleSuccessDone}>
            Done
          </Button>
        </div>
      );
    }

    if (step === 'upload') {
      return (
        <div className={css.footer}>
          <button type="button" className={css.editLink} onClick={handleClose}>
            Cancel
          </button>
        </div>
      );
    }

    if (step === 'confirm') {
      return (
        <div className={css.footer}>
          <Button
            type="primary"
            block
            onClick={handleRunReview}
            disabled={
              !isValidField(role) ||
              !isValidField(location) ||
              (isRemote && !isValidField(residence)) ||
              permitUnanswered
            }
          >
            Review my resume <ArrowRightOutlined />
          </Button>
          <button type="button" className={css.editLink} onClick={handleBackToUpload}>
            ← Back to upload
          </button>
        </div>
      );
    }

    if (step === 'review') {
      if (reviewLoading) {
        return null;
      }

      if (reviewError === 'expired') {
        return (
          <div className={css.footer}>
            <Button type="primary" block icon={<ReloadOutlined />} onClick={handleReupload}>
              Re-upload resume
            </Button>
          </div>
        );
      }

      if (reviewError === 'unavailable') {
        // Tailoring works without a review (the editor opens with empty fix
        // cards, comments still work) — keep the entry point available.
        return (
          <div className={css.footer}>
            <Button type="primary" block onClick={handleGeneratePlan} disabled={tailorEntering}>
              Continue without review <ArrowRightOutlined />
            </Button>
            <Button block loading={tailorEntering} onClick={handleEnterTailor}>
              Tailor my resume
            </Button>
            <button type="button" className={css.editLink} onClick={handleRunReview}>
              Try again
            </button>
          </div>
        );
      }

      return (
        <div className={css.footer}>
          <Button type="primary" block loading={tailorEntering} onClick={handleEnterTailor}>
            Tailor my resume
          </Button>
          <Button block onClick={handleGeneratePlan} disabled={tailorEntering}>
            Continue to coverage <ArrowRightOutlined />
          </Button>
          <Button
            block
            icon={<ReloadOutlined />}
            onClick={handleReupload}
            disabled={tailorEntering}
          >
            Re-upload improved resume
          </Button>
          <button type="button" className={css.editLink} onClick={handleBackToConfirm}>
            ← Edit role / location
          </button>
        </div>
      );
    }

    // step === 'plan'
    if (planLoading) {
      return null;
    }

    if (planError) {
      return (
        <div className={css.footer}>
          <Button type="primary" block onClick={handleGeneratePlan}>
            Try again
          </Button>
          <button type="button" className={css.editLink} onClick={handleBackToConfirm}>
            ← Edit role / location
          </button>
        </div>
      );
    }

    // At-capacity (empty plan): nothing to start — let the user close and go
    // manage their existing searches.
    if (planItems.length === 0) {
      return (
        <div className={css.footer}>
          <Button type="primary" block onClick={handleClose}>
            Got it
          </Button>
        </div>
      );
    }

    return (
      <div className={css.footer}>
        <Button
          type="primary"
          block
          loading={batchCreating}
          disabled={selectedCount === 0}
          onClick={handleCreatePack}
        >
          {selectedCount === 0
            ? 'Select at least one agent'
            : `Start ${selectedCount} ${selectedCount === 1 ? 'search' : 'searches'}`}
        </Button>
        {stashedAgents.length > 0 ? (
          <button
            type="button"
            className={css.editLink}
            onClick={restorePlan}
            disabled={batchCreating}
          >
            ↺ Bring back the suggested searches
          </button>
        ) : planItems.length > 1 ? (
          <button
            type="button"
            className={css.editLink}
            onClick={collapseToSeed}
            disabled={batchCreating}
          >
            Just my exact search
          </button>
        ) : null}
        <button
          type="button"
          className={css.editLink}
          onClick={handleBackToConfirm}
          disabled={batchCreating}
        >
          ← Edit role / location
        </button>
      </div>
    );
  };

  return (
    <Drawer
      title="Create New Search"
      placement="right"
      width="min(480px, 94vw)"
      onClose={handleClose}
      open={open}
      afterOpenChange={(visible) => { if (!visible) onAfterClose?.(); }}
      className={css.searchDrawer}
      footer={renderFooter()}
    >
      <div className={css.body}>
        {/* The rail marks progress through the creation steps; the success
            panel is past all of them, so showing a stepper there would only
            invite backtracking into a spent flow — hide it. */}
        {!creationSuccess && (
          <div className={css.stepBand}>
            <StepProgress current={step} />
          </div>
        )}

        <div className={css.stepScroll}>
          {creationSuccess && (
            <div className={css.successStep} role="status">
              <CheckCircleOutlined className={css.successIcon} />
              <h3>We're on it.</h3>
              <p className={css.successCount}>{creationSuccess.summary}</p>
              {/* The one thing someone needs the second after clicking Create
                  is "this takes hours, don't sit here waiting" — so the wait
                  leads, and the ongoing cadence drops to a separate quieter line
                  instead of competing with it. Wording tracks
                  SearchProgressDrawer's "First results can take a few hours" so
                  the two screens sound like one product.

                  The middle line is the answer to "so do I keep checking?", and
                  it is a real backend behaviour, not a promise we invented:
                  beast-api sends a readiness email when the LAST search of a
                  creation cohort finishes its first crawl
                  (searches/service.py notify_searches_ready, fired from
                  tasks.py). Hence "they" and one email, not one per search — a
                  five-search pack gets a single note once they have all landed.

                  "as soon as they land" is conditional on purpose. That email is
                  SKIPPED_EMPTY when a cohort found nothing, on the grounds that
                  an empty "your searches are ready" is worse than silence — so
                  the copy must not read as "you will get an email either way". */}
              <p className={css.successBody}>
                First matches usually <strong>take a few hours</strong>.
                <br />
                We'll <strong>email you as soon as they land</strong>.
                <br />
                You'll find them here, and in your daily summary.
              </p>
              {/* The steady state, and the one number that has to be right:
                  it is this user's own daily budget, NOT per search. The
                  trailing "across all your searches" is the entire correction
                  — without it a 5-search pack reads as 5x this figure.

                  "about", never "up to": the first-run floor means someone who
                  creates several searches in a day genuinely exceeds their cap
                  (up to 37 at the default). An upper bound we knowingly break
                  is worse than no number — the first person to get 14 after
                  being promised "up to 10" concludes it's broken. */}
              <p className={css.successCadence}>
                After that,{' '}
                {creationSuccess.dailyTotal != null ? (
                  <>
                    about{' '}
                    <strong>
                      {creationSuccess.dailyTotal} new matches a day
                    </strong>
                  </>
                ) : (
                  <>new matches once a day</>
                )}
                <br />
                across all your searches.
              </p>
            </div>
          )}

          {!creationSuccess && step === 'upload' && (
            <div className={css.uploadStep}>
              <h3>Upload your resume</h3>
              <p className={css.subtitle}>
                We'll extract your desired role and location.
              </p>

              <Spin spinning={loading} tip="Analyzing your resume...">
                <Dragger
                  accept={RESUME_ACCEPT}
                  showUploadList={false}
                  beforeUpload={handleFileUpload}
                  onDrop={handleResumeDrop}
                  disabled={loading}
                  className={css.dragger}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Drag & drop your resume here</p>
                  <p className="ant-upload-hint">
                    or click to browse. Supports PDF or DOCX files.
                  </p>
                </Dragger>
              </Spin>

              <p className={css.uploadSafetyNote}>
                Uploading is private and has no side effects — nothing starts
                until you press 'Start searches' at the last step.
              </p>
            </div>
          )}

          {step === 'confirm' && (
            <div className={css.confirmStep}>
              <h3>Confirm search details</h3>

              <div className={css.uploadedFile}>
                <CheckCircleOutlined className={css.checkIcon} />
                <span>Resume uploaded: {fileName}</span>
              </div>

              <div className={css.field}>
                <label>Job Title *</label>
                <Input
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="e.g., Software Engineer"
                  size="large"
                  maxLength={100}
                />
              </div>

              <div className={css.field}>
                <label id="search-mode-label">Search type</label>
                {/* One control, two states, the same shape the tailoring editor
                    uses for its template switch. Segmented renders real radio
                    inputs, so assistive tech gets a radiogroup and two named
                    radios without a wrapper. */}
                <Segmented
                  className={css.modeSwitch}
                  aria-labelledby="search-mode-label"
                  block
                  options={[
                    { label: 'Local search', value: 'local' },
                    { label: 'Remote search', value: 'remote' },
                  ]}
                  value={isRemote ? 'remote' : 'local'}
                  onChange={(value) => setIsRemote(value === 'remote')}
                />
                <p className={css.fieldHint}>
                  {isRemote
                    ? 'Remote jobs from employers in the destination, judged against where you live.'
                    : 'Jobs near one place you will work from.'}
                </p>
              </div>

              <div className={css.field}>
                <label>{isRemote ? 'Destination *' : 'Location *'}</label>
                <Input
                  value={location}
                  onChange={(e) => changeLocation(e.target.value)}
                  placeholder={
                    isRemote ? 'e.g., United States, Europe, Worldwide' : 'e.g., San Francisco, CA'
                  }
                  size="large"
                  maxLength={100}
                />
                {isRemote && (
                  <>
                    <p className={css.fieldHint}>
                      The country or region whose employers you want to work for.
                    </p>
                    {/* The chosen preset reads as chosen; a typed destination
                        leaves all three quiet. */}
                    <Space size="small" wrap className={css.presets}>
                      {DESTINATION_PRESETS.map((preset) => (
                        <Button
                          key={preset}
                          size="small"
                          type={sameCountry(location, preset) ? 'primary' : 'default'}
                          aria-pressed={sameCountry(location, preset)}
                          onClick={() => changeLocation(preset)}
                        >
                          {preset}
                        </Button>
                      ))}
                    </Space>
                  </>
                )}
              </div>

              {isRemote && (
                <div className={css.field}>
                  <label htmlFor="search-residence">Where are you based *</label>
                  <Input
                    id="search-residence"
                    value={residence}
                    onChange={(e) => setResidence(e.target.value)}
                    placeholder="e.g., Israel"
                    size="large"
                    maxLength={100}
                  />
                </div>
              )}

              {asksPermit && (
                <div className={css.field}>
                  {/* The info button sits beside the label, not inside it: a
                      button inside a <label> would swallow clicks on the text. */}
                  <div className={css.questionRow}>
                    <label id="search-permit-question">
                      Do you have a permit to work in {location.trim()}?
                    </label>
                    <Tooltip title={PERMIT_HINT} trigger={['hover', 'focus', 'click']}>
                      <button type="button" className={css.infoButton} aria-label="Why we ask">
                        <InfoCircleOutlined aria-hidden />
                      </button>
                    </Tooltip>
                  </div>
                  {/* One hint does both jobs: "permit" read as a document makes a
                      citizen answer No (and a No hides every role that asks for
                      work authorization), and this is the one field the resume
                      cannot fill. The disabled Continue button says the rest. */}
                  <p className={css.fieldHint}>
                    Yes if you're a citizen or resident there, or already hold a work permit
                    for it. Your resume can't tell us this one.
                  </p>
                  <div role="radiogroup" aria-labelledby="search-permit-question">
                    <Radio.Group
                      className={css.permitAnswer}
                      optionType="button"
                      buttonStyle="solid"
                      value={hasWorkPermit === null ? undefined : hasWorkPermit ? 'yes' : 'no'}
                      onChange={(e) => setHasWorkPermit(e.target.value === 'yes')}
                    >
                      <Radio.Button value="yes">Yes</Radio.Button>
                      <Radio.Button value="no">No</Radio.Button>
                    </Radio.Group>
                  </div>
                </div>
              )}

              <div className={css.field}>
                <label>Draft language</label>
                {/* The always-visible fieldHint below carries the auto-language
                    explanation, so the picker's hover tooltip is redundant here. */}
                <LanguagePicker
                  value={draftLanguage}
                  onChange={setDraftLanguage}
                  size="large"
                  className={css.languagePicker}
                  showAutoHint={false}
                />
                <p className={css.fieldHint}>
                  We'll write your applications in the job's language when your CV
                  shows you can — otherwise English.
                </p>
              </div>

              <p className={css.hint}>
                These were extracted from your resume. Feel free to adjust if needed.
              </p>
            </div>
          )}

          {step === 'review' && (
            <ResumeReviewStep
              review={review}
              previousReadiness={previousReadiness}
              loading={reviewLoading}
              error={reviewError}
            />
          )}

          {/* creationSuccess is only ever set while on the plan step — gate it
              so the spent plan doesn't render beneath the success panel. */}
          {step === 'plan' && !creationSuccess && (
            <>
              {resumeVersionId && !planLoading && (
                <div className={css.attachedBand} role="status">
                  <CheckCircleOutlined /> Tailored resume attached
                </div>
              )}
              <CoveragePlanStep
              items={planItems}
              loading={planLoading}
              error={planError}
              usedSlots={quota?.used ?? 0}
              maxPerUser={quota?.maxPerUser ?? 0}
              maxPerBatch={quota?.maxPerBatch ?? 0}
              remaining={quota?.remaining ?? 0}
              selectedCount={selectedCount}
              canAdd={canAdd}
              disabled={batchCreating}
              fixedLocation={isRemote ? location.trim() : undefined}
              onRemove={removePlanItem}
              onEditCommit={editPlanItem}
              onAdd={addPlanItem}
              />
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default SearchDrawer;
